import { useEffect, useRef, useState } from 'react';

interface BulkImportEditableCellProps {
  value: string;
  readOnly: boolean;
  placeholder?: string;
  inputType?: 'text' | 'email';
  mutedWhenEmpty?: boolean;
  onCommit: (next: string) => void;
}

export function BulkImportEditableCell({
  value,
  readOnly,
  placeholder = '—',
  inputType = 'text',
  mutedWhenEmpty = false,
  onCommit,
}: BulkImportEditableCellProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const display = value.trim() || placeholder;
  const isEmpty = !value.trim();

  const commit = () => {
    setEditing(false);
    const next = draft.trim();
    if (next !== value.trim()) onCommit(next);
  };

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  if (readOnly) {
    return (
      <span
        className={`bulk-import__cell-text${isEmpty && mutedWhenEmpty ? ' bulk-import__cell-text--muted' : ''}`}
      >
        {display}
      </span>
    );
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        type="text"
        inputMode={inputType === 'email' ? 'email' : undefined}
        autoComplete="off"
        spellCheck={inputType === 'email' ? false : undefined}
        className="bulk-import__cell-input"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            commit();
          }
          if (e.key === 'Escape') {
            e.preventDefault();
            cancel();
          }
        }}
      />
    );
  }

  return (
    <button
      type="button"
      className={`bulk-import__cell-edit${isEmpty && mutedWhenEmpty ? ' bulk-import__cell-edit--muted' : ''}`}
      onClick={() => setEditing(true)}
    >
      {display}
    </button>
  );
}
